import { useState, useEffect, useContext, createContext, useCallback } from 'react'
// Phase B3: access token in memory + bootstrap from the httpOnly refresh cookie.
import {
  getAccessToken, setAccessToken, clearAccessToken, bootstrap, refreshWasInconclusive, refusalEndsSession,
  sessionKeptThroughBusyMinute,
} from '../shared/api/tokenStore'
import { identityService } from '../shared/api'
import { asMessage } from '../shared/utils/errorDetail'
import { APP_STORAGE_PREFIX } from '../config/config.js';
import { ANNOUNCEMENT_ACK_KEY, RETIRED_ANNOUNCEMENT_ACK_KEYS } from '../shared/constants/announcement.js'
import { SESSION_KEYS, startSession } from '../shared/api/sessionPolicy'
import { accountLanguageChanged, cacheAccountLanguage } from '../shared/i18n/accountLanguage'

/**
 * THERE ARE TWO useAuth HOOKS IN THIS APP AND THIS IS THE BIG ONE — the context provider mounted in
 * App.jsx and consumed by ~40 files. The other is src/frontend/src/shared/hooks/useAuth.js, a
 * standalone hook imported by exactly three files: modules/projects/pages/ProjectsList.jsx,
 * .../ProjectDetail.jsx and .../LumpSumReleaseDetail.jsx. Those three are the project MONEY
 * screens, so a change to canViewFinancial HERE does not reach them. See the header of that file
 * for the field-by-field diff between the two.
 */

// Create authentication context
const AuthContext = createContext({})

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Initialize authentication on mount
  useEffect(() => {
    initializeAuth()
  }, [])

  // Initialize authentication state
  const initializeAuth = async () => {
    try {
      const storedUser = localStorage.getItem(`${APP_STORAGE_PREFIX}_user`)
      const lastValidated = localStorage.getItem(`${APP_STORAGE_PREFIX}_last_validated`)

      // Phase B3 — THE landmine of this migration. The access token is in memory, so on a page
      // reload it is always null even though the session is perfectly valid (the httpOnly refresh
      // cookie survived). The old `if (token && storedUser)` gate would therefore be false on
      // EVERY reload and silently drop the user to logged-out. Mint a token from the cookie first
      // and treat *that* as the signal. Only attempt it when a stored user exists, so a genuine
      // logged-out visitor doesn't fire a pointless refresh on every page load.
      let token = getAccessToken()
      if (!token && storedUser) {
        token = await bootstrap()
      }
      // No token, but the refresh met a 429 / restart / dropped connection rather than a refusal:
      // the session is alive and bootstrap() has kept the stored user. Stay signed in on it; the
      // shared client mints a token on the next request. Until 21 Sep this fell through to "signed
      // out" and the page landed on /login in any busy minute.
      const sessionAlive = !!token || (!!storedUser && refreshWasInconclusive())

      if (sessionAlive && storedUser) {
        // Set user from storage immediately, normalizing role to lowercase
        const parsedUser = JSON.parse(storedUser)
        if (parsedUser?.role) parsedUser.role = parsedUser.role.toLowerCase()
        setUser(parsedUser)

        // An admin can set somebody's language FOR them — the onboarding path the owner asked for,
        // because a person who does not read English cannot be expected to find an English settings
        // screen to say so. That write lands in the database and was invisible to a session already
        // open, EVEN ACROSS A FULL RELOAD: nothing on this path ever re-read the user row
        // (`/auth/verify-token` answers `{valid, user_id}` and nothing else, and getProfile has no
        // call sites). So the foreman standing at the admin's desk refreshed and nothing happened.
        //
        // Deliberately NOT awaited — a language is not worth delaying the app behind a round trip —
        // and deliberately narrow: one field, from an endpoint that returns only that field, so a
        // profile-shape change can never clobber role or permissions on a live session. It also runs
        // on every boot rather than on the 5-minute validateToken throttle, because "he refreshed and
        // nothing happened" is exactly the case it exists for.
        reconcileAccountLanguage(parsedUser)

        // Only validate token if it hasn't been validated in the last 5 minutes
        const shouldValidate = !lastValidated || 
          (Date.now() - parseInt(lastValidated, 10)) > 5 * 60 * 1000
          
        // With no token (an inconclusive refresh, above) there is nothing to validate: the call would
        // go out unauthenticated, take a 401, and the catch below would read that as a refusal.
        if (shouldValidate && token) {
          try {
            await identityService.validateToken()
            // Update last validation timestamp
            localStorage.setItem(`${APP_STORAGE_PREFIX}_last_validated`, Date.now().toString())
          } catch (error) {
            console.error('Token validation failed during init:', error)
            // ONLY a refusal ends the session. A 429 (per-address limit), a 5xx (a restart) or a
            // dropped connection says nothing about it: keep the session, leave the timestamp so the
            // next page load checks again. Until 21 Sep any failure here signed the person out.
            // And a 401 that only echoes a busy minute is no verdict either: the shared client may
            // have had to refresh an expired token first, met a 429, and sent verify-token with no
            // token at all (review of 21 Sep).
            if (!refusalEndsSession(error) || sessionKeptThroughBusyMinute()) return
            // Token is invalid, clear storage
            clearAccessToken(); localStorage.removeItem(`${APP_STORAGE_PREFIX}_access_token`)
            localStorage.removeItem(`${APP_STORAGE_PREFIX}_user`)
            localStorage.removeItem(`${APP_STORAGE_PREFIX}_last_validated`)
            setUser(null)
            // An expired session removes the cached user WITHOUT a page load, exactly as a sign-out
            // does, and lands on /login the same way. Same signal, same reason (see the logout path).
            accountLanguageChanged()
          }
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
      setError('Failed to initialize authentication')
    } finally {
      setLoading(false)
    }
  }
  
  /**
   * Ask the server what this person's language is, and adopt it if the cached row is behind.
   *
   * Fire-and-forget on purpose (see the call site). Every failure mode ends the same way — the cached
   * preference stands — which is the correct answer offline, on an older backend that has no such
   * route, and on a stubbed `identityService` in a test.
   */
  const reconcileAccountLanguage = (cachedUser) => {
    if (typeof identityService.getMyLanguage !== 'function') return
    Promise.resolve()
      .then(() => identityService.getMyLanguage())
      .then((data) => {
        const answer = data?.language
        const next = typeof answer === 'string' && answer ? answer : null
        if (next === (cachedUser?.language ?? null)) return
        cacheAccountLanguage(next)
        setUser((prev) => (prev ? { ...prev, language: next } : prev))
        // LanguageProvider is above <App/> and read this row at mount; it has no other way to learn.
        accountLanguageChanged()
      })
      .catch(() => { /* the cached preference stands */ })
  }

  // Validate authentication explicitly - useful for sensitive operations
  const validateAuthentication = useCallback(async () => {
    try {
      setLoading(true)
      const token = getAccessToken()
      const storedUser = localStorage.getItem(`${APP_STORAGE_PREFIX}_user`)
      const lastValidated = localStorage.getItem(`${APP_STORAGE_PREFIX}_last_validated`)
      
      if (!storedUser || (!token && !refreshWasInconclusive())) {
        console.log('No stored token or user found')
        setUser(null)
        return false
      }
      if (!token) {
        // The refresh met a 429 / restart / dropped connection: the session is alive, just without a
        // token this minute. Keep the person where they are; the next request mints one.
        setUser(JSON.parse(storedUser))
        return true
      }
      
      // Check if we need to validate with backend
      // Only validate token if it hasn't been validated in the last minute
      // This reduces API calls while still ensuring relatively fresh validation for sensitive operations
      const shouldValidateWithBackend = !lastValidated || 
        (Date.now() - parseInt(lastValidated, 10)) > 60 * 1000
      
      if (shouldValidateWithBackend) {
        try {
          console.log('Validating token with backend...')
          await identityService.validateToken()
          
          // Update last validation timestamp
          localStorage.setItem(`${APP_STORAGE_PREFIX}_last_validated`, Date.now().toString())
          
          // Ensure user state is set correctly (re-hydrate from localStorage)
          setUser(JSON.parse(storedUser))
          
          console.log('Token validated successfully with backend')
          return true
        } catch (error) {
          console.error('Token validation failed:', error)

          // Same rule as the page-load check: only a refusal ends the session. On a 429 / restart /
          // dropped connection keep the cached user and answer "still valid" - every screen the
          // caller opens is authorised by the server regardless, so this only decides whether to
          // throw the person out, and "the server was busy" is not a reason to.
          // Including the 401 that only echoes a busy minute - the case the Admin screen met in
          // review: token lapsed inside the idle window, its refresh met a 429, verify-token went out
          // with no token and came back 401.
          if (!refusalEndsSession(error) || sessionKeptThroughBusyMinute()) {
            setUser(JSON.parse(storedUser))
            return true
          }

          // Clear auth state on validation failure
          clearAccessToken(); localStorage.removeItem(`${APP_STORAGE_PREFIX}_access_token`)
          localStorage.removeItem(`${APP_STORAGE_PREFIX}_user`)
          localStorage.removeItem(`${APP_STORAGE_PREFIX}_last_validated`)
          setUser(null)
          accountLanguageChanged()   // see initializeAuth: the cached user just went, with no reload

          return false
        }
      } else {
        console.log('Using cached token validation')
        
        // Ensure user state is set correctly (re-hydrate from localStorage)
        setUser(JSON.parse(storedUser))
        
        return true
      }
    } catch (error) {
      console.error('Auth validation error:', error)
      return false
    } finally {
      setLoading(false)
    }
  }, []) // user state is read from localStorage inside the callback, not from closure

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.login(credentials)
      const { access_token, refresh_token, user: userData } = response

      // Normalize role to lowercase (handles any legacy uppercase values)
      if (userData?.role) userData.role = userData.role.toLowerCase()

      // Store token and user data
      setAccessToken(access_token)
      // Phase B1: the refresh token is set as an httpOnly cookie by the login response (same-origin, so
      // the browser stores it) — do NOT persist it to localStorage where XSS could steal it. Clear any
      // legacy copy from a pre-migration session.
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_refresh_token`)
      localStorage.setItem(`${APP_STORAGE_PREFIX}_user`, JSON.stringify(userData))
      localStorage.setItem(`${APP_STORAGE_PREFIX}_last_validated`, Date.now().toString())
      // Stamps BOTH clocks: last-activity, and the session-start anchor the absolute cap counts
      // from. This is the only place a session begins, so it is the only place that anchor is set —
      // nothing later may move it, or "twelve hours regardless of activity" would mean nothing.
      startSession()
      
      // Set user state
      setUser(userData)
      
      // Immediately validate the token to ensure it works
      console.log('Login successful - validating token...')
      try {
        await identityService.validateToken()
        console.log('Token validated after login')
      } catch (validationError) {
        console.error('Token validation failed after login:', validationError)
        // We'll still continue since the login itself was successful
      }
      
      // Clear any stored location state to prevent redirecting to previous pages
      if (window.history.state && window.history.state.from) {
        console.log('Clearing stored location state to ensure redirect to profile')
        window.history.replaceState({}, document.title)
      }
      
      return { success: true, user: userData }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Logout function
  const logout = async () => {
    console.log('Logout initiated')
    try {
      // Call the backend logout API to revoke the token
      const token = getAccessToken()
      if (token) {
        console.log('Sending logout request to backend...')
        const response = await identityService.logout()
        console.log('Logout API response:', response)
      } else {
        console.log('No token found to revoke')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clean up local storage regardless of API success
      console.log('Clearing local storage and user state')
      clearAccessToken(); localStorage.removeItem(`${APP_STORAGE_PREFIX}_access_token`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_refresh_token`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_user`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_last_validated`)
      localStorage.removeItem(`${APP_STORAGE_PREFIX}_last_activity`)
      localStorage.removeItem(SESSION_KEYS.sessionStarted)
      // Temporary post-update announcement: clear the ack so it re-shows next login.
      localStorage.removeItem(ANNOUNCEMENT_ACK_KEY)
      RETIRED_ANNOUNCEMENT_ACK_KEYS.forEach(k => localStorage.removeItem(k))
      // F-L4: purge any auto-saved bid drafts (confidential pricing) so they don't outlive the session
      // on a shared device (covers both the old bid_draft_<id> and the new per-user key formats).
      Object.keys(localStorage).filter(k => k.startsWith('bid_draft_')).forEach(k => localStorage.removeItem(k))
      setUser(null)
      setError(null)
      // The cached user row above carries the language, and LanguageProvider sits outside this
      // provider reading that row (see shared/i18n/accountLanguage.js). It never unmounts, so
      // without this the sign-in page keeps rendering in the language of somebody who has left.
      // The device's own choice is NOT removed here — it belongs to the browser, not the person.
      accountLanguageChanged()
      console.log('Logout completed')
    }
  }

  // Register function (admin only)
  const register = async (userData) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.register(userData)
      return { success: true, data: response }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Request password reset
  const requestPasswordReset = async (email) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.requestPasswordReset(email)
      return { success: true, data: response }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password reset request failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Reset password with token
  const resetPassword = async (token, newPassword) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.resetPassword(token, newPassword)
      return { success: true, data: response }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password reset failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.updateProfile(profileData)
      const updatedUser = { ...user, ...response }
      
      localStorage.setItem(`${APP_STORAGE_PREFIX}_user`, JSON.stringify(updatedUser))
      setUser(updatedUser)
      
      return { success: true, user: updatedUser }
    } catch (error) {
      // A 422's detail is a LIST; as a React child it blanks the page. Words only.
      const errorMessage = asMessage(error.response?.data?.detail) || 'Profile update failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.changePassword(currentPassword, newPassword)
      return { success: true, message: response.message }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Password change failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }
  
  // Update user role (admin only)
  const updateUserRole = async (userId, role) => {
    try {
      setLoading(true)
      setError(null)

      const response = await identityService.updateUserRole(userId, role)
      return { success: true, user: response }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Role update failed'
      setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // Check if user is authenticated
  // Phase B3: `user` is the durable signal; the in-memory token is empty until bootstrap.
  const isAuthenticated = !!user

  // Check if user has specific role or higher (Deprecated in favor of permissions)
  const hasRole = (role) => {
    // Keep this only for backward compatibility during transition if needed
    // system_admin is a PLATFORM (cross-tenant) role — a tenant admin/master_admin must NEVER satisfy it,
    // or the platform Super Admin console is exposed to tenant users. Require an exact match for it, BEFORE
    // the tenant-admin convenience short-circuit below. (Server-side routes are the authoritative gate;
    // this closes the client-side exposure.)
    if (role === 'system_admin') return user?.role === 'system_admin';
    // Master admin and admin satisfy lesser (tenant-scoped) role gates
    if (user?.role === 'master_admin' || user?.role === 'admin') return true;
    return user?.role === role;
  }

  /**
   * Tier-2 permission check.
   * ADMIN and MASTER_ADMIN bypass all permission checks and always return true.
   * All other users need the explicit permission string in their token/profile.
   * @param {string} permission - e.g. 'project:view_financial', 'timekeeping.approve'
   */
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'master_admin') return true;
    const perms = user.permissions || [];
    return perms.includes(permission);
  }

  // Check if user is admin or higher (master_admin)
  const isAdmin = useCallback(() => {
    return user?.role === 'admin' || user?.role === 'master_admin';
  }, [user])

  // Check if user is specifically a master admin
  const isMasterAdmin = () => {
    return user?.role === 'master_admin';
  }

  // Check if user is manager or above (Deprecated, use permissions)
  const isManager = () => {
    return hasRole('manager') || hasPermission('project:update');
  }

  // Check if user is estimator or above (Deprecated, use permissions)
  const isEstimator = () => {
    return hasPermission('bid:create_bid') || hasPermission('bid:manage_rates');
  }

  // Check if user is a bid requestor (Deprecated, use permissions)
  const isBidRequestor = () => {
    return hasPermission('bid:submit_bid_request');
  }

  // Check if user is specifically an employee
  const isEmployee = () => {
    return user?.role === 'employee' || user?.role === 'manager';
  }

  /**
   * Check if user can view financial data.
   *
   * THIS IS PRESENTATION, NOT A CONTROL. It decides what is DRAWN. It has never decided what is
   * SENT, and nothing in a browser can.
   *
   * `user` here is the record this provider read out of
   * `localStorage.getItem(`${APP_STORAGE_PREFIX}_user`)` on mount (initializeAuth, above) — the
   * same object shared/api/client.js:157 hands back as getCurrentUser(). It lives in the browser's
   * own storage, so the person at the keyboard can edit it in the console and change the answer
   * this function gives. Adding 'project:view_financial' to it makes a money column appear; it does
   * not make the server put money in the response.
   *
   * The boundary is the server. A caller without project:view_financial gets the money fields
   * blanked in the response body itself (src/shared/auth/money.py), and the response reports which
   * ones it blanked in `withheld_fields`. So the honest reading of a null money field is:
   *
   *     null AND named in withheld_fields  ->  WITHHELD. Say "Hidden".
   *     null AND not named                 ->  genuinely EMPTY. Say "—" / "$0".
   *
   * Do not infer that from this function — ask the payload, via shared/utils/withheld.js
   * (`isWithheld(project, 'contract_amount')`) or <WithheldValue>. This check and the server's
   * answer CAN disagree — a stale cached permission list is enough — and when they do the payload
   * is the one telling the truth.
   *
   * Keep the check. Hiding a column somebody may not see is good design. It is just not the thing
   * that keeps the number off the wire.
   *
   * Must match the identity-issued permission (project:view_financial); the old
   * 'projects.financial' matched nothing.
   */
  const canViewFinancial = () => {
    return hasPermission('project:view_financial');
  }

  // Labor hours/progress without dollars. Financial viewers see labor too (superset); a foreman
  // gets project:view_labor to track hours on the Labor tab while cost columns stay hidden.
  const canViewLabor = () => {
    return hasPermission('project:view_labor') || hasPermission('project:view_financial');
  }

  // Check if user can create/edit/delete projects. Real backend perms are colon-form
  // (project:create/update/...); 'projects.manage' matched nothing in any JWT.
  const canManageProjects = () => {
    return hasPermission('project:create') || hasPermission('project:update') ||
      hasRole('manager') || hasRole('admin') || hasRole('master_admin');
  }

  // Check if user can assign employees to projects
  const canAssignEmployees = () => {
    return hasPermission('project:assign') || canManageProjects();
  }

  // Clear error
  const clearError = () => setError(null)

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    register,
    requestPasswordReset,
    resetPassword,
    changePassword,
    updateProfile,
    updateUserRole,
    isAuthenticated,
    hasRole,
    hasPermission,
    isAdmin,
    isMasterAdmin,
    isManager,
    isEstimator,
    isBidRequestor,
    isEmployee,
    canViewFinancial,
    canViewLabor,
    canManageProjects,
    canAssignEmployees,
    clearError,
    validateAuthentication,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
